import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/redux/store';
import { projectsApi } from 'app/services/projects';

import { questionnairesApi } from 'app/services/questionnaires';
import { TQuestionnaireData } from 'app/services/responseTypes';
import lodashGet from 'lodash.get';
import { PrepareQuestionnaireState } from '../../create/PrepareQuestionnaire/slice';
import { AdditionalQuestionsRoutes } from './additionalQuestions';
import { BusinessInfoRoutes } from './businessInfo';
import { IncomeRoutes } from './income';
import { NonUsFinancialAccountsRoutes } from './nonUsFinancialAccounts';
import { PersonalDetailsRoutes } from './personal_details';
import {
  buildRoutes,
  flattenRoutes,
  initialState,
  unpack,
} from './routesBuilder';
import { SoleProprietorshipRoutes } from './soleProprietorship';
import { SpouseIncomeRoutes } from './spouseIncome';
import { TaxesDeductionsRoutes } from './taxesDeductions';

export interface BaseQuestionnaireRoute {
  title: string;
  path: string;
  isValid?: boolean;
  toggle?: boolean;
}

export interface FlatRoute {
  mainTab: string;
  mainTabTitle: string;
  subTab?: string;
  subTabTitle?: string;
  leaf?: string;
  leafTitle?: string;
  dotPath: string;
  slashPath: string;
  isValid?: boolean;
}

export interface QuestionnaireRoutes {
  routes: {
    personal_details: PersonalDetailsRoutes;
    income: IncomeRoutes;
    spouseIncome: SpouseIncomeRoutes;
    taxesDeductions: TaxesDeductionsRoutes;
    nonUsFinancialAccounts: NonUsFinancialAccountsRoutes;
    notes: BaseQuestionnaireRoute & { notes: BaseQuestionnaireRoute };
    summary: BaseQuestionnaireRoute & {
      summary: BaseQuestionnaireRoute;
      documentsChecklist: BaseQuestionnaireRoute;
    };
    additionalQuestions: AdditionalQuestionsRoutes;
    businessInfo: BusinessInfoRoutes;
    soleProprietorship: SoleProprietorshipRoutes;
  };
  flatRoutes: FlatRoute[];
  flatMap: { [slashPath: string]: number };
}

const questionnaireRoutesSlice = createSlice({
  name: 'questionnaireRoutes',
  initialState,
  reducers: {
    setTabValidationState(
      state,
      action: PayloadAction<{
        dotPath: string;
        isValid: boolean;
      }>,
    ) {
      const { isValid, dotPath } = action.payload;

      const [mainTab, subTab, leafTab] = unpack(state.routes, dotPath);
      if (mainTab) {
        state.flatRoutes.filter((x) => x.mainTab === mainTab.path);

        if (leafTab) leafTab.isValid = isValid;

        if (subTab) subTab.isValid = isValid;

        mainTab.isValid =
          isValid === false
            ? false
            : state.flatRoutes.filter(
                (sibling) =>
                  sibling.mainTab === mainTab.path &&
                  sibling.isValid === false &&
                  sibling.dotPath !== dotPath,
              ).length === 0;

        // state.routes = buildRoutes(state.routes);
        // lodashSet(state.routes, `${dotPath}.isValid`, isValid);
        [state.flatRoutes, state.flatMap] = flattenRoutes(state.routes);
      }
    },
    collapseSubTab(state, action: PayloadAction<string>) {
      lodashGet(state.routes, action.payload).toggle = false;
    },
    expandSubTab(state, action: PayloadAction<string>) {
      lodashGet(state.routes, action.payload).toggle = true;
    },
    toggleSubTab(state, action: PayloadAction<string>) {
      const subTab = lodashGet(state.routes, action.payload);
      subTab.toggle = !subTab.toggle;
    },
    updateQuestionnaireRoutes(
      state,
      action: PayloadAction<PrepareQuestionnaireState>,
    ) {
      state.routes = buildRoutes({ prepareQuestionnaire: action.payload });
      [state.flatRoutes, state.flatMap] = flattenRoutes(state.routes);
    },
    updateQuestionnaireRoutesWithQuestionnaireState(
      state,
      action: PayloadAction<TQuestionnaireData>,
    ) {
      state.routes = buildRoutes({
        prepareQuestionnaire: action.payload.prepareQuestionnaire,
        taxQuestionnaire: action.payload.taxQuestionnaire,
      });
      [state.flatRoutes, state.flatMap] = flattenRoutes(state.routes);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      questionnairesApi.endpoints.getQuestionnaireById.matchFulfilled,
      (state, { payload }) => {
        state.routes = buildRoutes(payload.questionnaire.data);
        [state.flatRoutes, state.flatMap] = flattenRoutes(state.routes);
      },
    );
    builder.addMatcher(
      projectsApi.endpoints.getProjectById.matchFulfilled,
      (state, { payload }) => {
        state.routes = buildRoutes(payload.project.questionnaire.data);
        [state.flatRoutes, state.flatMap] = flattenRoutes(state.routes);
      },
    );
  },
});

export const {
  setTabValidationState,
  collapseSubTab,
  toggleSubTab,
  expandSubTab,
  updateQuestionnaireRoutes,
  updateQuestionnaireRoutesWithQuestionnaireState,
} = questionnaireRoutesSlice.actions;
export default questionnaireRoutesSlice.reducer;

export const selectQuestionnaireRoutes = (state: RootState) =>
  state.taxQuestionnaire.questionnaireRoutes;

export const selectIsQuestionnaireValidState = (state: RootState) => {
  return state.taxQuestionnaire.questionnaireRoutes.flatRoutes.every(
    (r) => r.isValid !== false,
  );
};
